import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useT, LanguageSwitcher } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { EnvelopeSimple } from "@phosphor-icons/react";

const BG = "https://static.prod-images.emergentagent.com/jobs/a38c17ed-f1e9-46fe-9e7c-fe4731e3e917/images/585c338ce7226b5954ccd5e96883f9bac1c70cbfa6424f609ab458b88f5c70dc.jpeg";

export default function ForgotPassword() {
  const t = useT();
  const nav = useNavigate();
  const goBack = () => (window.history.state?.idx > 0 ? nav(-1) : nav("/"));
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/auth/forgot-password", { email: email.trim() }); }
    catch (err) { console.debug("forgot-password (generic response)", err); }
    finally { setLoading(false); setSent(true); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      <div className="absolute top-4 right-4 z-20"><LanguageSwitcher /></div>
      <div className="hidden md:block relative overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 scale-105 blur-[1px]" />
        <div className="absolute inset-0 bg-tatami-overlay" />
        <div className="absolute inset-0 bg-kukkiwon-mesh opacity-70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div>
            <button type="button" onClick={goBack} className="flex items-center gap-2 font-heading font-extrabold text-2xl tracking-tight text-foreground hover:opacity-80 transition-opacity" data-testid="forgot-logo-back">
              <Logo size={30} className="text-brand-yellow" /> KIHAP
            </button>
            <div className="text-muted-foreground text-xs uppercase tracking-[0.25em] mt-1 ml-9">Tournament Software for Taekwondo</div>
          </div>
          <div>
            <h1 className="font-heading text-6xl md:text-7xl font-black uppercase leading-[0.9] tracking-tight">Taekwondo<br />tournaments,<br /><span className="text-brand-yellow">simplified!</span></h1>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        {sent ? (
          <div className="w-full max-w-sm space-y-5 text-center" data-testid="forgot-sent">
            <div className="mx-auto w-14 h-14 rounded-full bg-brand-yellow/15 flex items-center justify-center">
              <EnvelopeSimple size={28} className="text-brand-yellow" />
            </div>
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">{t("forgot.sentTitle")}</h2>
            <p className="text-sm text-muted-foreground">{t("forgot.sentMsg")} <strong className="text-foreground">{email}</strong></p>
            <Link to="/login" className="inline-block text-sm text-white underline underline-offset-4" data-testid="forgot-sent-login">{t("forgot.back")}</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="forgot-form">
            <div>
              <button type="button" onClick={goBack} className="md:hidden flex items-center gap-2 font-heading font-extrabold text-xl mb-8 text-foreground" data-testid="forgot-logo-back-mobile">
                <Logo size={24} className="text-brand-yellow" /> KIHAP
              </button>
              <h2 className="font-heading text-3xl font-bold uppercase tracking-tight">{t("forgot.title")}</h2>
              <p className="text-muted-foreground mt-2 text-sm">{t("forgot.desc")}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="forgot-email">{t("common.email")}</Label>
              <Input id="forgot-email" data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <Button type="submit" className="w-full h-11 font-semibold" data-testid="forgot-submit" disabled={loading}>
              {loading ? t("login.accessing") : t("forgot.cta")}
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              <Link to="/login" className="text-white underline underline-offset-4" data-testid="forgot-back-link">{t("forgot.back")}</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
